import { dungApiThat, goiApi, goiApiTepHoacLienKet } from '../api/http';
import { mayChuKhoOcr } from '../../mock/ocr';
import type { OcrFile, OcrFilePage, OcrFileState, OcrUploadResult } from '../../modules/kho-ocr/types';
import type { DocTranscript, TepXemTruoc } from '../../modules/xu-ly/types';
import { GOC_OCR, giaLap, giaLapDanhSach, kiemTrang, maHoa } from './ocr-chung';

const GOC = `${GOC_OCR}/ocr-files`;
const TRANG_RONG: OcrFilePage = { items: [], next_cursor: null };

export interface LocKhoOcr {
  search?: string;
  state?: OcrFileState;
  limit: number;
  cursor?: string;
}

const goc = (id: string) => `${GOC}/${maHoa(id)}`;
const ban = (id: string, versionId: string) => `${goc(id)}/versions/${maHoa(versionId)}`;

/**
 * Kho bản OCR (module `ocr`, đợt 4): tài liệu đã đọc chữ để tái dùng khi tạo chứng từ. Mỗi hàm chọn
 * **lúc gọi** giữa API thật và máy chủ giả (`mock/ocr.ts`). Sai trạng thái của bản OCR:
 * `409 OCR_TRANSCRIPT_CONFLICT` với câu backend nói cách xử lý.
 */
export const khoOcrRepository = {
  /** `GET /ocr-files` — mới nhất trước. */
  danhSach: (loc: LocKhoOcr): Promise<OcrFilePage> =>
    dungApiThat()
      ? goiApi<OcrFilePage>(GOC, { query: { ...loc } }).then(kiemTrang)
      : giaLapDanhSach(() => mayChuKhoOcr.danhSach(loc), TRANG_RONG),

  /** `GET /ocr-files/{id}` — kèm mọi bản OCR và chứng từ đang dùng từng bản. */
  chiTiet: (id: string): Promise<OcrFile> =>
    dungApiThat() ? goiApi<OcrFile>(goc(id)) : giaLap(() => mayChuKhoOcr.chiTiet(id)),

  /** `POST /ocr-files/upload` → `202`, multipart `files`, `reader_key`, `skip_if_done`. Chỉ PDF và ảnh. */
  taiLen: (files: File[], reader_key: string, skip_if_done: boolean): Promise<OcrUploadResult> => {
    if (!dungApiThat()) {
      return giaLap(() =>
        mayChuKhoOcr.taiLen(
          files.map((f) => ({ ten: f.name, mime: f.type, kichThuoc: f.size })),
          reader_key,
          skip_if_done,
        ),
      );
    }
    const bieuMau = new FormData();
    for (const f of files) bieuMau.append('files', f, f.name);
    bieuMau.append('reader_key', reader_key);
    bieuMau.append('skip_if_done', String(skip_if_done));
    return goiApi<OcrUploadResult>(`${GOC}/upload`, { method: 'POST', bieuMau });
  },

  /** `POST /ocr-files/{id}/versions` → `202`: OCR lại thành bản mới; trả tài liệu đọc lại. */
  docLai: (id: string, reader_key: string): Promise<OcrFile> =>
    dungApiThat()
      ? goiApi<unknown>(`${goc(id)}/versions`, { method: 'POST', body: { reader_key } }).then(() => goiApi<OcrFile>(goc(id)))
      : giaLap(() => mayChuKhoOcr.docLai(id, reader_key)),

  /** `POST /ocr-files/{id}/versions/{v}/pin` — chỉ bản đã đọc xong. */
  ghim: (id: string, versionId: string): Promise<OcrFile> =>
    dungApiThat()
      ? goiApi<OcrFile>(`${ban(id, versionId)}/pin`, { method: 'POST' })
      : giaLap(() => mayChuKhoOcr.ghim(id, versionId)),

  /** `DELETE /ocr-files/{id}/versions/{v}/pin`. */
  boGhim: (id: string, versionId: string): Promise<OcrFile> =>
    dungApiThat()
      ? goiApi<OcrFile>(`${ban(id, versionId)}/pin`, { method: 'DELETE' })
      : giaLap(() => mayChuKhoOcr.boGhim(id, versionId)),

  /** `POST /ocr-files/{id}/versions/{v}/cancel` — chỉ bản đang đọc. */
  huyDoc: (id: string, versionId: string): Promise<OcrFile> =>
    dungApiThat()
      ? goiApi<OcrFile>(`${ban(id, versionId)}/cancel`, { method: 'POST' })
      : giaLap(() => mayChuKhoOcr.huyDoc(id, versionId)),

  /** `DELETE /ocr-files/{id}/versions/{v}` → `204`. Đang đọc hay đang gắn chứng từ: `409`. */
  xoaBan: (id: string, versionId: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(ban(id, versionId), { method: 'DELETE' })
      : giaLap(() => mayChuKhoOcr.xoaBan(id, versionId)),

  /** `DELETE /ocr-files/{id}` → `204`. */
  xoaTep: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(goc(id), { method: 'DELETE' }) : giaLap(() => mayChuKhoOcr.xoaTep(id)),

  /** `GET /ocr-files/{id}/versions/{v}/transcript` — chỉ bản đã đọc xong. */
  banChep: (id: string, versionId: string): Promise<DocTranscript> =>
    dungApiThat()
      ? goiApi<DocTranscript>(`${ban(id, versionId)}/transcript`)
      : giaLap(() => mayChuKhoOcr.banChep(id, versionId)),

  /** `GET /ocr-files/{id}/file` — liên kết ký (`files/sign`) hoặc nội dung tệp (bản dev). */
  tep: async (id: string): Promise<TepXemTruoc> => {
    if (!dungApiThat()) {
      const t = await giaLap(() => mayChuKhoOcr.tep(id));
      return { kieu: 'noi-dung', ...t };
    }
    const t = await goiApiTepHoacLienKet(`${goc(id)}/file`);
    return t.kieu === 'lien-ket' ? t : { ...t, file_name: '' };
  },
};
